// @ts-strict-ignore

/**
 * @module BubbleConferenceRecap
 */

import { FileDescriptor } from 'rainbow-web-sdk/models/common/fileDescriptor';
import { FILE_STORAGE_SVC, FileStorageService } from '../services/files/fileStorage.service';
import { BubbleConferenceRecapService, CONFERENCE_RECAP_SVC } from '../services/conference/bubbleConferenceRecap.service';
import { BubbleService, LOGGER_SVC, LoggerService, MainService } from '../services';
import { FILEDOCUMENT_SVC, FileDocumentService } from '../services/fileDocument/fileDocument.service';
import { FileDocument } from './fileDocument.model';
import { RBEvent } from './event.model';
import { Subject, Subscription, filter } from 'rxjs';
import { User } from './user.model';
import { BUBBLE_SVC } from '../services/bubbles/bubble.service';
import { Bubble } from './bubble.model';
import { CONTACT_SVC, ContactService } from '../services/core/contact.service';
import { allSettled } from '../utils/allSettled';

export class BubbleConferenceRecapTranscriptMessage {
    public id!: string;
    public start!: number;
    public end!: number;
    public text!: string;
    public speaker?: User;
    public isActive = false;
    public searched = false;

    public static create(start: number, end: number, text: string, speaker: User): BubbleConferenceRecapTranscriptMessage {
        const transcriptionMessage = new BubbleConferenceRecapTranscriptMessage();
        transcriptionMessage.start = start;
        transcriptionMessage.end = end;
        transcriptionMessage.text = text;
        transcriptionMessage.speaker = speaker;
        return transcriptionMessage;
    }
}

export interface RecapViewer {
    type: "bubble" | "user";
    user?: User;
    bubble?: Bubble;
}

export type BubbleConferenceRecapDocumentTypes = 'poster' | 'chat' | 'audio' | 'video' | 'summary' | 'transcriptionText' | 'transcriptionJson' | 'subtitles' | 'chapters';

export interface BubbleConferenceRecap {

    /**
     * Unique identifier for the conference recap.
     */
    id?: string;

    /**
     * Start date of the recap in ISO format.
     */
    startDate?: string;

    /**
     * Stop date of the recap in ISO format.
     */
    stopDate?: string;

    /**
     * List of records associated with the conference recap.
     * @internal
     */
    records?: BubbleConferenceRecapFile[];

    /**
     * Identifier of the bubble (conference) associated with the recap.
     */
    bubbleId?: string;

    /**
     * Name of the bubble (conference) associated with the recap.
     */
    bubbleName?: string;

    /**
     * Topic of the bubble (conference) associated with the recap.
     */
    bubbleTopic?: string;

    /**
     * Status of the conference recap (RecapStatusEnum).
     */
    status: RecapStatusEnum;

    /**
     * Identifier of the user who created the recap.
     */
    userId?: string;

    /**
     * URL of the bubble's avatar.
     */
    avatarURL?: string;

    /**
     * TRUE If summary is asked for this recording
     */
    summaryAsked?: boolean;

    /**
     * Status of the summary (e.g., 'none', 'failed', 'release_complete', 'in-progress').
     * NB !!! summaryStatus is basically the same as the transcriptionStatus (it's actually the same status for both);
     */
    summaryStatus?: RecapStatusEnum;

    /**
     * TRUE If transcription is asked for this recording
     */
    transcriptionAsked?: boolean;

    /**
     * Status of the transcription (e.g., 'none', 'failed', 'release_complete', 'in-progress').
     */
    transcriptionStatus?: RecapStatusEnum;

    /**
     * In case of error during the transcription, here we can find the details of the error;
     * Information sent by the backed is as follows:
        const TRANSCRIPTION_REASON = {
        NONE: 'none',
        API_KEY_ERROR: 'apikey_error',
        RATE_LIMIT_EXCEEDED: 'rate_limit_exceeded',
        INSUFFICIENT_CREDITS: 'insufficient_credits',
        FORBIDDEN_ACCESS: 'forbidden_access',
        SERVER_ERROR: 'server_error',
        INVALID_DATA: 'invalid_data',
        INTERNAL_ERROR: 'internal_error',
        };
     */
    transcriptionErrorReason: RecapErrorReasonEnum;

    /**
     * Status of the meeting recording (e.g., 'release_complete', 'in-progress').
     */
    recordingStatus?: RecapStatusEnum;

    /**
     * Define auto expiry of the recording
     * false : A meeting record and eventually its transcription and its summary are always available until the recording is deleted.
     * true : That is to say only the transcription and the summary will remain. The date timeToLiveDate is taken in account to schedule a cleaning task.
     */
    isEphemeral?: boolean;

    /**
     * Property related to the  auto expiry of the recording
     * false : The meeeting recording is available;
     * true : The meeting recording has expired and is no longer available. The transcription and summary can still be seen (if available)
     */
    isCleaned?: boolean;

    /**
     * Date (as string) from which a cleaning task might be scheduled. Linked to isEphemeral property
     */
    timeToLiveDate: string;

    /**
     * List of actions currently possible for this recap
     */
    actions: BubbleConferenceRecapActions;

    /**
     * Delete conference recap
     */
    delete(): Promise<void>;

    /**
     * Get recap document by type
     * @param type - the document type to retreive
     */
    getFileDocument(type: BubbleConferenceRecapDocumentTypes): Promise<FileDocument | undefined>;

    getTranscriptMessages(): Promise<BubbleConferenceRecapTranscriptMessage[]>;
    getSpeakerRanges(): any[];
    getSummary(): Promise<string | undefined>;
    getSubtitlesUrl(): Promise<string | undefined>;
    getVideoUrl(): Promise<string | undefined>;
    getBubble(): Promise<Bubble>;
    getDuration(): number;
    getPublisher(): Promise<User>;
    getParticipants(): Promise<User[]>;

    /**
     * Permit to control if a recap has a doument of type 'type' or not
     * @param type - the document type to control
     */
    hasDocumentOfType(type: BubbleConferenceRecapDocumentTypes): boolean;

    /**
     * Debug Method to return a simplified JSON representation of the User object.
     * @internal
     */
    toJSON(): any;

    getRecapStatus(): BubbleConferenceRecapStatus;

    subscribe(handler: (event: RBEvent<BubbleConferenceRecapEvents>) => any, eventNames?: BubbleConferenceRecapEvents | BubbleConferenceRecapEvents[]): Subscription;

    /** @internal */
    associatedRecordingFiles?: FileDescriptor[];

    /** @internal */
    overallArchiveSize?: number;

    /** @internal */
    updateFromData(data: any): void;

    /** @internal */
    isSharedWith(destination: User | Bubble): boolean;

    /** @internal */
    dashFilesAvailable: boolean;

    /** @internal */
    downloadInProgress: boolean;

    /** @internal */
    viewers: any[];

    /**
     * Disable the auto delete for the recording video;
     * Related to property "isEphemeral"
     */
    disableEphemeral(): Promise<void>;

    /**
     * This API will create an archive (zip) will all files available in the recap; Once generated, the zip will be available in the user storage;
     * ATTENTION: In return you'll get the FileDocument that can be used to download the archive, but it will remain in the user storage; So consider removing it from the storage in order to save space;
     * Each call of this API will generate a new archive with !
     * @param archiveName - the name of the archive [Optional]
     */
    download(archiveName?: string): Promise<Document>;

    /**
     * Share recap with other users / bubbles
     * The list of user/bubbles that can see this recap can be found via the sharedWith() API
     */
    share(destination: User | Bubble): Promise<void>;

    /**
     * Unshare the recap with the given user / bubble
     */
    unshare(recapViewer: RecapViewer): Promise<void>;

    /**
     * Replace the summary content by the edited one.
     * @param newSummary - String representing the new summary.
     */
    replaceSummaryFile(newSummary: string): Promise<void>;

    /**
     * Get the current list of Users and Bubbles that the recap is being shared with
     */
    sharedWith(): RecapViewer[];

    /**
     * If this recap is mine or belongs to someone else (shared with me)
     */
    isMyRecap(): boolean;
}

export enum RecapErrorReasonEnum {
    NONE = 'none',
    API_KEY_ERROR = 'apikey_error',
    RATE_LIMIT_EXCEEDED = 'rate_limit_exceeded',
    INSUFFICIENT_CREDITS = 'insufficient_credits',
    FORBIDDEN_ACCESS = 'forbidden_access',
    SERVER_ERROR = 'server_error',
    INVALID_DATA = 'invalid_data',
    INTERNAL_ERROR = 'internal_error',
}

export interface BubbleConferenceRecapStatus {
    /**
     * The general status of the recording/recap
     */
    generalRecapStatus: RecapStatusEnum;

    /**
     * The status of the transcription
     */
    transcriptionStatus: RecapStatusEnum;

    /**
     * The status of the summary
     */
    summaryStatus: RecapStatusEnum;

    /**
     * In case of error, details about the error (if any)
     */
    errorReason: RecapErrorReasonEnum;
}

export enum RecapStatusEnum {
    /**
     * The operation has finished without error
     */
    READY = "release_complete",

    /**
     * The opration has failed
     */
    FAILED = "failed",

    /**
     * The opration is in progress (some files are available)
     */
    IN_PROGRESS = "in_progress",

    /**
     * The opration is initiated (no files are available yet)
     */
    INITIATED = "initiated",

    /**
     * The opration is not asked
     */
    NONE = "none"
}

/**
 * BubbleConference public events
 * @eventProperty
 */
export enum BubbleConferenceRecapEvents {
    /**
    * @eventProperty
    * This RBEvent is fired when the bubbleConferenceRecap status is updated
    */
    ON_STATUS_CHANGE = "ON_STATUS_CHANGE",

    /**
    * @eventProperty
    * This RBEvent is fired when the isEphemeral property is updated
    */
    ON_EPHEMERAL_CHANGE = "ON_EPHEMERAL_CHANGE",

    /**
    * @eventProperty
    * This RBEvent is fired when the recap actions property are updated
    */
    ON_RECAP_ACTIONS_CHANGE = "ON_RECAP_ACTIONS_CHANGE",

    /**
    * @eventProperty
    * This RBEvent is fired when the list of viewers have changed
    */
    ON_RECAP_VIEWERS_CHANGE = "ON_RECAP_VIEWERS_CHANGE"
}

export interface BubbleConferenceRecapActions {
    /**
     * Indicates if the recap can be download.
     * @readonly
     */
    download: boolean;

    /**
     * Indicates if the recap can be shared (or unshared) with other users/bubbles (owner only).
     * @readonly
     */
    share: boolean;

    /**
     * Indicates if the recap can be deleted (owner only).
     * @readonly
     */
    delete: boolean;

    /**
     * Indicates if the recap can be played.
     * @readonly
     */
    play: boolean;

    /**
     * Indicates if the recap summary can be modified (owner only).
     * @readonly
     */
    modifySummary: boolean;

    /**
     * Indicates if the recap transcript can be downloaded as text file.
     * @readonly
     */
    downloadTranscript: boolean;

    /**
     * Indicates if the recap auto delete option can be canceled (owner only).
     * @readonly
     */
    disableEphemeral: boolean;
}

/**
 * @internal
 */
export class BubbleConferenceRecapRB implements BubbleConferenceRecap {

    public id?: string;
    public startDate?: string;
    public stopDate?: string;
    public records?: BubbleConferenceRecapFile[];
    public bubbleId?: string;
    public bubbleName?: string;
    public bubbleTopic?: string;
    public status: RecapStatusEnum = RecapStatusEnum.NONE;
    public userId?: string;
    public avatarURL?: string;
    public associatedRecordingFiles?: FileDescriptor[];
    public overallArchiveSize?: number;
    public capabilities: BubbleConferenceRecapDocumentTypes[] = [];
    public summaryAsked: boolean = false;
    public summaryStatus: RecapStatusEnum = RecapStatusEnum.NONE;
    public transcriptionAsked: boolean = false;
    public transcriptionStatus: RecapStatusEnum = RecapStatusEnum.NONE;
    public transcriptionErrorReason: RecapErrorReasonEnum = RecapErrorReasonEnum.NONE;
    public recordingStatus: RecapStatusEnum = RecapStatusEnum.NONE;
    public isCleaned: boolean = false;
    public participantIds: any[] = [];

    private _isEphemeral: boolean = false;
    private publisher?: User;
    private bubble?: Bubble;
    private participants?: User[];
    public downloadInProgress: boolean = false;
    private participantListComplete: boolean = true;

    public isMyRecap(): boolean {
        return (this.userId === this.contactService?.userContact.dbId);
    }

    public sharedWith(): RecapViewer[] {
        return this._viewersList;
    }

    private async updateViewers() {
        //build the array from the current viewers
        const promises = [];
        this._viewersList = [];

        //if it's not my bubble, then we dont care about the list of viewers
        if (this.isMyRecap()) {
            this._viewers.forEach((viewer) => {
                try {
                //each viewer has type and db ID
                    if (viewer.type === 'room') { promises.push((async () => { const bubble = await this.bubbleService.getBubble(viewer.viewerId); this._viewersList.push({ type: 'bubble', bubble: bubble }); })()); }
                    if (viewer.type === 'user') { promises.push((async () => { const contact = await this.contactService.getContact(viewer.viewerId); this._viewersList.push({ type: 'user', user: contact }); })()); }
                }
                catch (error) {
                    this.loggerService?.debug(`[BubbleConferenceRecap] updateViewers error ${error?.message}`);
                }
            });

            await allSettled(promises);

            this.sendEvent(BubbleConferenceRecapEvents.ON_RECAP_VIEWERS_CHANGE);
        }
    }

    //this is the internal management of viewers, for SDK / Model we'll provide a map with Users / Bubbles
    private _viewers: any[] = [];
    private _viewersList: any[] = [];

    public get viewers(): any[] {
        return this._viewers;
    }

    private set viewers(viewers: any[]) {
        if (this._viewers?.length !== viewers?.length) {
            this._viewers = viewers;
            this.updateViewers();
        }
    }

    private _actions: BubbleConferenceRecapActions = {
        download: false,
        share: false,
        delete: false,
        play: false,
        modifySummary: false,
        downloadTranscript: false,
        disableEphemeral: false
    };

    public get actions(): BubbleConferenceRecapActions {
        return this._actions;
    }

    private set actions(actions: BubbleConferenceRecapActions) {
        //compare both capabilities ...
        let update = false;
        Object.keys(this._actions).forEach((key) => {
            if (this._actions[key] !== actions[key]) {
                update = true;
            }
        });
        if (update) {
            this._actions = actions;
            this.sendEvent(BubbleConferenceRecapEvents.ON_RECAP_ACTIONS_CHANGE);
        }
    }

    public get isEphemeral(): boolean {
        return this._isEphemeral;
    }

    public set isEphemeral(value: boolean) {
        if (this._isEphemeral !== value) {
            this._isEphemeral = value;
            this.sendEvent(BubbleConferenceRecapEvents.ON_EPHEMERAL_CHANGE);
        }
    }

    public timeToLiveDate: string;
    // For POC only
    // Dashed video files available
    public dashFilesAvailable: boolean = false;

    private mainService: MainService = MainService.getInstance();
    private fileStorageService?: any = this.mainService.inject<FileStorageService>(FILE_STORAGE_SVC);
    private confRecapService?: any = this.mainService.inject<BubbleConferenceRecapService>(CONFERENCE_RECAP_SVC);
    private documentService?: any = this.mainService.inject<FileDocumentService>(FILEDOCUMENT_SVC);
    private contactService?: any = this.mainService.inject<ContactService>(CONTACT_SVC);
    private bubbleService = MainService.getInstance().inject<BubbleService>(BUBBLE_SVC);
    private loggerService = this.mainService.inject<LoggerService>(LOGGER_SVC);
    private speakerRanges: any[] = [];
    private visualSpeakerRanges: any = [];
    private eventSubject: Subject<RBEvent<BubbleConferenceRecapEvents>> = new Subject<RBEvent<BubbleConferenceRecapEvents>>();

    private constructor() {}

    public static createFromData(data: any): BubbleConferenceRecap {
        const recap = new BubbleConferenceRecapRB();
        recap.updateFromData(data);
        return recap;
    }

    public subscribe(handler: (event: RBEvent<BubbleConferenceRecapEvents>) => any, eventNames?: BubbleConferenceRecapEvents | BubbleConferenceRecapEvents[]): Subscription {
        const observable = eventNames
            ? this.eventSubject.pipe(filter((event: RBEvent<BubbleConferenceRecapEvents>) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.eventSubject;

        return observable.subscribe((event: RBEvent<BubbleConferenceRecapEvents>) => {
            try { handler(event); }
            catch { /* */ }
        });
    }

    private sendEvent(name: BubbleConferenceRecapEvents, data?: any): void {
        this.eventSubject.next(RBEvent.create(name, data));
    }

    public updateFromData(data: any): void {
        if (data.id) this.id = data.id;
        if (data.recordingStartDate) this.startDate = data.recordingStartDate?.toString ? data.recordingStartDate?.toString() : data.recordingStartDate;
        if (data.recordingStopDate) this.stopDate = data.recordingStopDate;
        if (data.records) this.records = data.records;
        if (data.roomId) this.bubbleId = data.roomId;
        if (data.roomName) this.bubbleName = data.roomName;
        if (data.roomTopic) this.bubbleTopic = data.roomTopic;

        if (data.userId) this.userId = data.userId;
        if (data.associatedRecordingFiles) this.associatedRecordingFiles = data.associatedRecordingFiles;
        if (data.overallArchiveSize) this.overallArchiveSize = data.overallArchiveSize;
        if (data.summary) this.summaryAsked = data.summary;
        if (data.statusSummary) this.summaryStatus = data.statusSummary;
        if (data.transcription) this.transcriptionAsked = data.transcription;
        if (data.statusTranscription) this.transcriptionStatus = data.statusTranscription;
        if (data.transcriptionReason) this.transcriptionErrorReason = data.transcriptionReason;
        if (data.statusMeetingRecording) this.recordingStatus = data.statusMeetingRecording;

        if (data.isEphemeral !== undefined) this.isEphemeral = data.isEphemeral;
        if (data.timeToLiveDate) { this.timeToLiveDate = data.timeToLiveDate; }

        this.capabilities = [];
        this.records.forEach((recapFile: BubbleConferenceRecapFile) => {
            if (recapFile.fileName.includes('transcription.txt')) this.capabilities.push('transcriptionText');
            if (recapFile.fileName.includes('transcription.json')) this.capabilities.push('transcriptionJson');
            if (recapFile.fileName.includes('transcription.vtt')) this.capabilities.push('transcriptionJson');
            if (recapFile.fileName.includes('summary.txt')) this.capabilities.push('summary');
            if (recapFile.fileName.includes('chapter.json')) this.capabilities.push('chapters');
            if (recapFile.fileName.includes('Recording_banner')) this.capabilities.push('poster');
            if (recapFile.fileName.includes('chat.txt')) this.capabilities.push('chat');
            if (recapFile.fileName.includes('audio.m4a')) this.capabilities.push('audio');
            if (recapFile.fileName.includes('sharing.mp4')) this.capabilities.push('video');
        });

        // Handle participants informations
        if (data.participants) {
            this.participantIds = data.participants.reduce((acc: string[], participant: any) => {
                if (participant?.userId) {
                    acc.push(participant.userId);
                }
                return acc;
            }, []);
        }

        if (data.isCleaned !== undefined) {
            this.isCleaned = data.isCleaned;
        }

        if (data.status && this.status !== data.status) {
            this.status = data.status;
            this.sendEvent(BubbleConferenceRecapEvents.ON_STATUS_CHANGE);
        }

        //should we refresh from th
        if (data.isInitialized !== undefined) {
            this.participantListComplete = data.isInitialized;
        }

        if (data.viewers) {
            this.viewers = data.viewers;
        }

        this.updateActions();
    }

    private updateActions() {
        //consider that it's my recap for the moment
        const status = this.getRecapStatus();

        if (status.generalRecapStatus === RecapStatusEnum.READY) {
            //check if we still have m4a or mp4 files and that the file is ephemeral
            this.isCleaned = (this.isCleaned || !(this.records.find(record => record.typeMIME === "audio/m4a") || this.records.find(record => record.typeMIME === "video/mp4")));
        }

        const actions: BubbleConferenceRecapActions = {
            play: status.generalRecapStatus === RecapStatusEnum.READY,
            delete: this.isMyRecap() && (status.generalRecapStatus === RecapStatusEnum.READY || status.generalRecapStatus === RecapStatusEnum.FAILED),
            download: (status.generalRecapStatus === RecapStatusEnum.READY),
            share: this.isMyRecap() && (status.generalRecapStatus === RecapStatusEnum.READY),
            modifySummary: this.isMyRecap() && (status.generalRecapStatus === RecapStatusEnum.READY && status.summaryStatus === RecapStatusEnum.READY),
            downloadTranscript: (status.generalRecapStatus === RecapStatusEnum.READY && status.transcriptionStatus === RecapStatusEnum.READY),
            disableEphemeral: this.isMyRecap() && (this.isEphemeral && !this.isCleaned)
        };

        this.actions = actions;
    }

    public async share(destination: User | Bubble): Promise<void> {
        if (!this.actions.share) throw new Error("User not allowed to share recap");
        if (this.sharedWith().find((viewer) => {
            return (viewer.bubble?.dbId === destination.dbId || viewer.user?.dbId === destination.dbId);
        })) {
            return;
        }
        await this.confRecapService?.shareConferenceRecording(this, destination);
    }

    public async unshare(recapViewer: RecapViewer): Promise<void> {
        if (!this.actions.share) throw new Error("User not allowed to unshare recap");
        await this.confRecapService?.unshareConferenceRecording(this, recapViewer);
    }

    public async delete(): Promise<void> {
        if (!this.actions.delete) throw new Error("User not allowed to delete recap");
        await this.confRecapService?.deleteRecap(this);
    }

    public async download(archiveName: string = ''): Promise<Document> {
        if (!this.fileStorageService) throw new Error("File storage not available for this user");
        if (!this.actions.download) throw new Error("User not allowed to download recap");
        if (this.downloadInProgress) throw new Error("Download already in progress");

        this.downloadInProgress = true;

        //reset the download in 2 min in case something went wrong with the generation of the file
        setTimeout(() => {
            this.downloadInProgress = false;
        }, 120000);

        try {
            try {
                if (!archiveName) {
                    const date = new Date(this.startDate);
                    archiveName = `Recording_${date.getFullYear()}-${date.getMonth()}-${date.getDay()}`; }
            }
            catch {
                archiveName = `Recording_${this.startDate?.split(".")[0]}`;
            }

            const document = await this.documentService?.createZipFromDocuments(archiveName, this.records);
            return document;
        }
        catch (error) {
            this.downloadInProgress = false;
            throw error;
        }
    }

    public async getFileDocument(type: BubbleConferenceRecapDocumentTypes): Promise<FileDocument | undefined> {
        if (!this.fileStorageService) return undefined;
        let recapFile: BubbleConferenceRecapFile;
        switch (type) {
            case 'poster': recapFile = this.records.find(record => record.fileName.includes('Recording_banner')); break;
            case 'chat': recapFile = this.records.find(record => record.typeMIME === "text/plain" && !record.fileName.includes('-summary') && !record.fileName.includes(`-transcription`)); break;
            case 'audio': recapFile = this.records.find(record => record.typeMIME === "audio/m4a"); break;
            case 'video': recapFile = this.records.find(record => record.typeMIME === "video/mp4"); break;
            case 'transcriptionText': recapFile = this.records.find(record => record.typeMIME === "text/plain" && record.fileName.includes(`-transcription`)); break;
            case 'transcriptionJson': recapFile = this.records.find(record => record.typeMIME === "application/json" && record.fileName.includes(`-transcription`)); break;
            case 'summary': recapFile = this.records.find(record => record.typeMIME === "text/plain" && record.fileName.includes('-summary')); break;
            case 'subtitles': recapFile = this.records.find(record => record.typeMIME === "text/vtt"); break;
            case 'chapters': recapFile = this.records.find(record => record.fileName.includes('chapter.json')); break;
            default: break;
        }
        if (recapFile?.fileId) {
            const fileDescriptor = await this.fileStorageService.retrieveAndStoreOneFileDescriptor(recapFile?.fileId);
            let document: FileDocument = this.documentService.getDocumentById(recapFile.fileId);
            if (!document) document = this.documentService.createDocumentFromFileDescriptor(fileDescriptor);
            return document;
        }
        return undefined;
    }

    public async disableEphemeral(): Promise<void> {
        if (!this.fileStorageService) throw new Error("FileStorage is not enabled for this user");
        if (!this.actions.disableEphemeral) throw new Error("User not allowed to disableEphemeral");
        await this.fileStorageService.changeConferenceRecapEphemeral(this, false);
    }

    public hasDocumentOfType(type: BubbleConferenceRecapDocumentTypes): boolean {
        return this.capabilities.includes(type);
    }

    public isSharedWith(destination: User | Bubble): boolean {
        return !!(this.sharedWith().find((viewer) => {
            return (viewer.bubble?.dbId === destination.dbId || viewer.user?.dbId === destination.dbId);
        }));
    }

    private async updateFromServer() {
        try {
            await this.confRecapService?.updateRecapFromServer(this);
        }
        catch {
            return;
        }
    }

    public async getParticipants(): Promise<User[]> {
        try {
            if (this.participants) return this.participants;

            //check if we need to update from server
            //some data is missing in the events; Once we get "endDate" we can remove this I Think !
            if (!this.participantListComplete) {
                //we need to refresh the recap to get the full info about the participants and the talking time
                this.participantListComplete = true;
                await this.updateFromServer();
            }

            this.participants = await Promise.all(this.participantIds.map(async (participantId: string) => {
                return await this.contactService?.getContactByDBId(participantId);
            }));
            return this.participants;
        } catch { return []; }
    }

    public async getTranscriptMessages(): Promise<BubbleConferenceRecapTranscriptMessage[]> {
        try {
            // Get transcription JSON file
            const transcriptionFileDocument = await this.getFileDocument("transcriptionJson");
            const transcriptUrl = await transcriptionFileDocument?.getPublicUrl();
            if (!transcriptUrl) return [];
            const response: Response = await fetch(transcriptUrl, { method: "GET" });
            if (!response.ok) throw (response);
            const transcript: any = await response.json();

            // Fetch participant users if necessary
            await this.getParticipants();
            const speakerUsers = {};
            // Get the speaker users array
            Object.keys(transcript.mapping).forEach((key) => {
                //map the key to the user, as Chaps send key - value combination
                speakerUsers[key] = this.contactService?.getContactFromCache(transcript.mapping[key].id);
                if (!speakerUsers[key] && transcript.mapping[key].displayName) {
                    //create temp account
                    speakerUsers[key] = this.contactService?.createBasicContact(null, transcript.mapping[key].displayName);
                }
            });
            // Compute duration between first and last transcript message
            const duration = 1000 * (transcript.transcription.at(-1).end - transcript.transcription[0].start);
            const minIntervalForRangeCloseValue = Math.min(Math.max(duration / 100, 2000), 30000) / 1000;

            const speakerRanges = {};
            // Create the transcript message array
            const transcriptMessages = transcript.transcription.map((mess: any, index: number) => {
                const nextMessage = transcript.transcription[index + 1];
                const end = nextMessage ? nextMessage.start : mess.end;
                const message = BubbleConferenceRecapTranscriptMessage.create(mess.start, end, mess.text, speakerUsers[mess.speaker]);
                const currentSpeaker = speakerUsers[mess.speaker];
                const range = { start: mess.start, end: mess.end, color: currentSpeaker.color };

                // Handle speakerRange
                if (!speakerRanges[currentSpeaker.dbId]) {
                    speakerRanges[currentSpeaker.dbId] = { speaker: currentSpeaker, total: range.end - range.start, ranges: [range] };
                }
                else {
                    const currentSpeakerRanges = speakerRanges[currentSpeaker.dbId];
                    const lastRange = currentSpeakerRanges.ranges.at(-1);
                    if (mess.start - lastRange.end < minIntervalForRangeCloseValue) {
                        currentSpeakerRanges.total += range.end - lastRange.end;
                        lastRange.end = mess.end;
                    }
                    else {
                        currentSpeakerRanges.total += range.end - range.start;
                        currentSpeakerRanges.ranges.push(range);
                    }
                }
                return message;
            });

            this.visualSpeakerRanges = Object.values(speakerRanges); //.sort((speakA, speakB) => { return speakB.total - speakA.total; });

            return transcriptMessages;
        }
        catch { return []; }
    }

    public getSpeakerRanges(): any[] {
        return this.visualSpeakerRanges;
    }

    public async getSummary(): Promise<string | undefined> {
        try {
            // Get summary file
            const summaryFileDocument = await this.getFileDocument("summary");
            const summaryUrl = await summaryFileDocument.getPublicUrl();
            if (!summaryUrl) return undefined;
            const response: Response = await fetch(summaryUrl, { method: "GET" });
            if (!response.ok) throw (response);
            return await response.text();
        }
        catch { return undefined; }
    }

    public async getSubtitlesUrl(): Promise<string | undefined> {
        try {
            const subtitlesFileDocument = await this.getFileDocument("subtitles");
            return await subtitlesFileDocument.getPublicUrl();
        }
        catch { return undefined; }
    }

    public async getVideoUrl(): Promise<string | undefined> {
        try {
            const videoFileDocument = await this.getFileDocument("video");
            return await videoFileDocument.getPublicUrl();
        }
        catch { return undefined; }
    }

    public async getBubble(): Promise<Bubble> {
        if (!this.bubble) this.bubble = await this.bubbleService.getBubble(this.bubbleId);
        return this.bubble;
    }

    public async getPublisher(): Promise<User> {
        if (!this.publisher) this.publisher = await this.contactService?.getContactByDBId(this.userId);
        return this.publisher;
    }

    /**
     * API used to build a more user friendly information on the current situation with the recap
     */
    public getRecapStatus(): BubbleConferenceRecapStatus {
        const status: BubbleConferenceRecapStatus = {
            generalRecapStatus: RecapStatusEnum.NONE,
            transcriptionStatus: RecapStatusEnum.NONE,
            summaryStatus: RecapStatusEnum.NONE,
            errorReason: RecapErrorReasonEnum.NONE
        };

        //transcription has been asked, update current status
        if (this.transcriptionAsked && this.transcriptionStatus) { status.transcriptionStatus = this.transcriptionStatus; }
        if (this.summaryAsked && this.summaryStatus) { status.summaryStatus = this.summaryStatus; }
        if (this.transcriptionErrorReason) { status.errorReason = this.transcriptionErrorReason; }

        //for the general recap status, if we have at least the meeting recording that worked, show as ready
        if (this.status) {
            status.generalRecapStatus = this.status;

            //backend returns FAILED even when the recording is ready which is wrong; We need to show ready even
            //when we've at least the recording ready; Failed is when everything fails
            if (this.status === RecapStatusEnum.FAILED && this.recordingStatus === RecapStatusEnum.READY) {
                status.generalRecapStatus = RecapStatusEnum.READY;
            }
        }
        return status;
    }

    public getDuration(): number {
        const start = new Date(this.startDate);
        const end = new Date(this.stopDate);
        return (end.getTime() - start.getTime()) / 1000;
    }

    public toJSON(): any {
        const json: any = { id: this.id, bubbleId: this.bubbleId, status: this.status };
        json.records = this.records.map((record: any) => { return { fileId: record.fileId, fileName: record.fileName, typeMIME: record.typeMIME }; });
        return json;
    }

    public async replaceSummaryFile(newSummary: string): Promise<void> {
        const summaryFile: BubbleConferenceRecapFile = this.records.find(record => record.typeMIME === "text/plain" && record.fileName.includes('-summary'));
        const summaryDocument: FileDocument = await this.documentService.getDocumentById(summaryFile.fileId);
        summaryDocument.replaceContent(new Blob([newSummary], { type: 'text/plain' }));
        await this.documentService.uploadDocument(summaryDocument);
    }
}

/** @internal */
export class BubbleConferenceRecapFile {
    public fileId?: string;
    public fileName?: string;
    public md5sum?: string;
    public size?: number;
    public typeMIME?: string;

    /** @internal */
    public static createFromData(data: any): BubbleConferenceRecapFile {
        const confRecord = new BubbleConferenceRecapFile();
        if (data.fileId) confRecord.fileId = data.fileId;
        if (data.fileName) confRecord.fileName = data.fileName;
        if (data.md5sum) confRecord.md5sum = data.md5sum;
        if (data.size) confRecord.size = data.size;
        if (data.typeMIME) confRecord.typeMIME = data.typeMIME;
        return confRecord;
    }

    private constructor() {}
}
